"use client";

import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSmsSettings } from "@/actions/integrations/smsSettings";
import Icon from "@/components/library/Icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SmsSettings } from "@/types/settings";
import SmsSettingsPanel from "./SmsSettingsPanel";

type Status = "loading" | "connected" | "disconnected";

function fromNumberOf(settings: SmsSettings): string | null | undefined {
  if (settings.provider.provider === "twilio") return settings.provider.phoneNumber;
  if (settings.provider.provider === "directo") return settings.provider.fromPhoneNumber;
  return undefined;
}

export default function SmsIntegrationCard() {
  const { t } = useTranslation("partners");
  const { t: ct } = useTranslation("common");
  const [status, setStatus] = useState<Status>("loading");
  const [settings, setSettings] = useState<SmsSettings | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { success, data } = await getSmsSettings();
      if (success && data) {
        setSettings(data);
        setStatus("connected");
        return;
      }
    } catch {
      // Fall through to disconnected — a card should never crash the grid.
    }
    setSettings(null);
    setStatus("disconnected");
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const connected = status === "connected" && settings != null;

  return (
    <>
      <Card
        className={clsx(
          "relative",
          "flex",
          "flex-col",
          "shadow-none",
          "border-2",
          "border-gray-300",
          "dark:border-gray-700",
          "w-full",
          "min-w-sm"
        )}
        data-testid="sms-integration-card"
      >
        <CardHeader className={clsx("flex", "flex-col", "gap-2")}>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900">
              <Icon icon="message" className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex flex-col flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">{t("smsIntegration.title")}</h3>
                {connected && (
                  <Badge
                    variant="outline"
                    className="text-green-600 border-green-600 dark:text-green-400 dark:border-green-400"
                  >
                    {ct("connected")}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className={clsx("flex", "flex-col", "flex-1")}>
          <CardContentBody status={status} settings={settings} t={t} />
        </CardContent>
        <CardFooter>
          <Button
            onClick={() => setDialogOpen(true)}
            disabled={status === "loading"}
            className="w-full"
          >
            {connected ? t("smsIntegration.manage") : t("smsIntegration.connect")}
          </Button>
        </CardFooter>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("smsIntegration.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("smsIntegration.dialogDescription")}</DialogDescription>
          </DialogHeader>
          <SmsSettingsPanel onChanged={refresh} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function CardContentBody({
  status,
  settings,
  t,
}: {
  status: Status;
  settings: SmsSettings | null;
  t: (key: string, options?: Record<string, string>) => string;
}) {
  if (status === "loading") {
    return <p className="text-600 text-sm">{t("smsIntegration.loading")}</p>;
  }

  if (settings) {
    const fromNumber = fromNumberOf(settings);
    return (
      <div className="flex flex-col gap-1 text-sm text-600">
        <p>
          {t("smsIntegration.connectedDescription", {
            provider: t(`smsIntegration.provider.${settings.provider.provider}`),
          })}
        </p>
        {fromNumber && (
          <p className="text-xs mt-1">{t("smsIntegration.phone", { phoneNumber: fromNumber })}</p>
        )}
      </div>
    );
  }

  return <p className="text-sm">{t("smsIntegration.disconnectedDescription")}</p>;
}
